import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Inject,
  InjectionToken,
  Input,
  Optional,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { ConnectedOverlayDirective } from '../../core/overlay/overlay-directives';
import { DropdownItemComponent } from './dropdown-item/dropdown-item.component';
import { NestedTemplatesHistoryService } from './dropdown-nested-templates/nested-templates-history.service';

export enum DropdownWidth {
  SmallXX = '150px',
  SmallX = '200px',
  Small = '250px',
  Medium = '300px',
  Large = '350px',
  LargeX = '400px',
  LargeXX = '450px',
  LargeXXX = '500px',
  LargeXXXX = '550px',
  LargeXXXXX = '600px',
  FullWidth = 'unset',
}

/**
 * @description Wrapper element to ensure proper styling.
 *
 *
 *  __> INPUTS:__
 *  - __width__: _DropdownWidth (enum)_ - use __FullWidth__ to set dropdown width to trigger element (e.g. input) width
 *
 */

export const DROPDOWN_ITEM_CONTENT_CHILDREN = new InjectionToken<
  BehaviorSubject<QueryList<DropdownItemComponent>>
>('Dropdown Item Content Children');

@Component({
  selector: 'rt-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.width]': 'width',
  },
  providers: [
    NestedTemplatesHistoryService,
    { provide: DROPDOWN_ITEM_CONTENT_CHILDREN, useValue: new BehaviorSubject(null) },
  ],
})
export class DropdownComponent {
  @Input() public width: DropdownWidth = DropdownWidth.Medium; // predefined dropdown widths

  @ViewChild('defaultTpl', { static: true }) public defaultTpl: TemplateRef<any>;

  public template: TemplateRef<any>;

  @ContentChildren(DropdownItemComponent)
  private dropdownItemElements: QueryList<DropdownItemComponent>;

  constructor(
    @Optional() private connectedOverlayDirective: ConnectedOverlayDirective,
    private cdRef: ChangeDetectorRef,
    private nestedTemplatesHistory: NestedTemplatesHistoryService,
    @Inject(DROPDOWN_ITEM_CONTENT_CHILDREN)
    private dropdownItemContentChildren$: BehaviorSubject<QueryList<DropdownItemComponent>>,
  ) {}

  public setNestedTemplate(templateRef: TemplateRef<any>) {
    this.template = this.nestedTemplatesHistory.pushState(templateRef);
    this.forceViewUpdate();
  }

  public prevTemplate(): void {
    this.template = this.nestedTemplatesHistory.back();
    this.forceViewUpdate();
  }

  public ngOnInit(): void {
    this.template = this.nestedTemplatesHistory.pushState(this.defaultTpl);
    this.cdRef.detectChanges();
  }

  public ngAfterContentInit(): void {
    if (this.dropdownItemElements.length)
      this.dropdownItemContentChildren$.next(this.dropdownItemElements);
  }

  private forceViewUpdate(): void {
    this.cdRef.detectChanges();
    this.connectedOverlayDirective?.updatePositionWithoutRedrawing();
  }
}
